import type {
  RequestRateBySegment,
  RequestLengthBucket,
  MonthlySegmentTrend,
  SceneSegmentCross,
} from '../../types/styling-analysis.js';

/** @graph-connects none */
const SEGMENT_LABELS: Record<string, string> = {
  first: '初回利用',
  second_third: '2-3回目',
  fourth_plus: '4回目以降',
  repeat: '継続利用',
};

/** @graph-connects none */
const BUCKET_LABELS: Record<string, string> = {
  short: '短い (〜20文字)',
  medium: '中程度 (21〜60文字)',
  long: '長い (61文字〜)',
};

/** @graph-connects none */
interface RequestInsightsPanelProps {
  requestRateBySegment: RequestRateBySegment[];
  requestLengthVsRestyling: RequestLengthBucket[];
  monthlySegmentTrends: MonthlySegmentTrend[];
  sceneBySegment: SceneSegmentCross[];
}

/**
 * @graph-node {Function}
 * @graph-stack ac-mens-hub
 * @graph-domain Styling
 * @graph-business Display McKinsey-level request insights: segment-based fill rates, length vs restyling correlation, monthly segment trends, and scene cross-tabs with So What annotations.
 * @graph-connects none
 */
export function RequestInsightsPanel({
  requestRateBySegment,
  requestLengthVsRestyling,
  monthlySegmentTrends,
  sceneBySegment,
}: RequestInsightsPanelProps) {
  return (
    <div className="space-y-10">
      <SegmentFillRateSection data={requestRateBySegment} />
      <LengthRestylingSection data={requestLengthVsRestyling} />
      <MonthlySegmentSection data={monthlySegmentTrends} />
      <SceneCrossSection data={sceneBySegment} />
    </div>
  );
}

/** @graph-connects none */
function SegmentFillRateSection({ data }: { data: RequestRateBySegment[] }) {
  if (data.length === 0) {
    return (
      <SectionShell title="利用回数別リクエスト記入率">
        <EmptyState />
      </SectionShell>
    );
  }

  const firstSegment = data.find((d) => d.segment === 'first');
  const repeatSegments = data.filter((d) => d.segment !== 'first');
  const firstRate =
    firstSegment && firstSegment.totalOrders > 0
      ? (firstSegment.withRequest / firstSegment.totalOrders) * 100
      : 0;
  const repeatRate = repeatSegments.reduce((sum, s) => sum + s.withRequest, 0);
  const repeatTotal = repeatSegments.reduce((sum, s) => sum + s.totalOrders, 0);
  const repeatPct = repeatTotal > 0 ? (repeatRate / repeatTotal) * 100 : 0;
  const diff = repeatPct - firstRate;

  return (
    <SectionShell title="利用回数別リクエスト記入率">
      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="border-b border-[var(--color-border)]">
              <th className="px-3 py-2 text-left font-medium text-[var(--color-text-secondary)]">
                セグメント
              </th>
              <th className="px-3 py-2 text-right font-medium text-[var(--color-text-secondary)]">
                オーダー数
              </th>
              <th className="px-3 py-2 text-right font-medium text-[var(--color-text-secondary)]">
                リクエスト記入
              </th>
              <th className="px-3 py-2 text-right font-medium text-[var(--color-text-secondary)]">
                記入率
              </th>
              <th className="px-3 py-2 text-right font-medium text-[var(--color-text-secondary)]">
                シーン指定
              </th>
              <th className="px-3 py-2 text-right font-medium text-[var(--color-text-secondary)]">
                再選定
              </th>
              <th className="px-3 py-2 text-right font-medium text-[var(--color-text-secondary)]">
                再選定率
              </th>
              <th className="px-3 py-2 text-right font-medium text-[var(--color-text-secondary)]">
                平均文字数
              </th>
            </tr>
          </thead>
          <tbody>
            {data.map((row) => {
              const fillRate = row.totalOrders > 0 ? (row.withRequest / row.totalOrders) * 100 : 0;
              const restylingRate =
                row.totalOrders > 0 ? (row.withRestyling / row.totalOrders) * 100 : 0;
              return (
                <tr
                  key={row.segment}
                  className="border-b border-[var(--color-border)] hover:bg-[var(--color-bg-elevated)]"
                >
                  <td className="px-3 py-2 font-medium text-[var(--color-text-primary)]">
                    {SEGMENT_LABELS[row.segment] ?? row.segment}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">{String(row.totalOrders)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{String(row.withRequest)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-[var(--color-accent-primary)]">
                    {`${String(Math.round(fillRate))}%`}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">{String(row.withScene)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{String(row.withRestyling)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{`${String(Math.round(restylingRate))}%`}</td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {String(Math.round(row.avgRequestLength))}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <SoWhat>
        {diff > 5
          ? `継続利用者は初回比+${String(Math.round(diff))}ptリクエスト記入率が高い → サービス理解が進むとリクエストが具体化する傾向。初回ユーザーにはリクエスト例文の提示が有効。`
          : diff < -5
            ? `初回利用者の記入率が継続比+${String(Math.round(Math.abs(diff)))}pt高い → 初回は期待値が高くリクエストを書くが、継続時に減少。リクエストの効果実感を伝える施策が必要。`
            : '初回と継続利用者のリクエスト記入率に大きな差はない。安定したリクエスト行動が確認できる。'}
      </SoWhat>
    </SectionShell>
  );
}

/** @graph-connects none */
function LengthRestylingSection({ data }: { data: RequestLengthBucket[] }) {
  if (data.length === 0) {
    return (
      <SectionShell title="リクエスト具体度 × 再選定率">
        <EmptyState />
      </SectionShell>
    );
  }

  const maxCount = Math.max(1, ...data.map((d) => d.totalCount));

  const shortBucket = data.find((d) => d.bucket === 'short');
  const longBucket = data.find((d) => d.bucket === 'long');
  const shortRate =
    shortBucket && shortBucket.totalCount > 0
      ? (shortBucket.restylingCount / shortBucket.totalCount) * 100
      : 0;
  const longRate =
    longBucket && longBucket.totalCount > 0
      ? (longBucket.restylingCount / longBucket.totalCount) * 100
      : 0;

  return (
    <SectionShell title="リクエスト具体度 × 再選定率">
      <div className="space-y-4">
        {data.map((bucket) => {
          const restylingRate =
            bucket.totalCount > 0 ? (bucket.restylingCount / bucket.totalCount) * 100 : 0;
          return (
            <div key={bucket.bucket} className="space-y-1">
              <div className="flex items-center justify-between text-sm">
                <span className="text-[var(--color-text-primary)]">
                  {BUCKET_LABELS[bucket.bucket] ?? bucket.bucket}
                </span>
                <span className="text-xs text-[var(--color-text-muted)]">
                  {String(bucket.totalCount)}件 / 再選定率{' '}
                  {`${String(Math.round(restylingRate * 10) / 10)}%`} / 平均
                  {String(Math.round(bucket.avgLength))}文字
                </span>
              </div>
              <div className="flex gap-1">
                <div className="h-6 flex-1 overflow-hidden rounded-md bg-[var(--color-bg-elevated)]">
                  <div
                    className="flex h-full items-center rounded-md bg-[var(--color-accent-primary)] px-2 text-xs text-white"
                    style={{
                      width: `${String(Math.max(3, (bucket.totalCount / maxCount) * 100))}%`,
                    }}
                  >
                    {String(bucket.totalCount)}
                  </div>
                </div>
                <div className="w-20 shrink-0">
                  <div className="h-6 overflow-hidden rounded-md bg-[var(--color-bg-elevated)]">
                    <div
                      className="flex h-full items-center rounded-md bg-red-500/70 px-1 text-xs text-white"
                      style={{ width: `${String(Math.max(3, Math.min(100, restylingRate * 3)))}%` }}
                    >
                      {`${String(Math.round(restylingRate))}%`}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      <SoWhat>
        {longRate < shortRate
          ? `具体的なリクエスト（長文）ほど再選定率が低い（短文${String(Math.round(shortRate))}% → 長文${String(Math.round(longRate))}%）。リクエストの具体化を促すUI改善がスタイリング満足度向上に直結する。`
          : '現状データではリクエスト文字数と再選定率に明確な相関は見られない。他の要因（シーン指定、画像添付等）の影響を分析する必要がある。'}
      </SoWhat>
    </SectionShell>
  );
}

/** @graph-connects none */
function MonthlySegmentSection({ data }: { data: MonthlySegmentTrend[] }) {
  if (data.length === 0) {
    return (
      <SectionShell title="月別リクエスト記入率推移（初回 vs 継続）">
        <EmptyState />
      </SectionShell>
    );
  }

  const months = [...new Set(data.map((d) => d.month))].sort();
  const firstData = data.filter((d) => d.segment === 'first');
  const repeatData = data.filter((d) => d.segment === 'repeat');

  return (
    <SectionShell title="月別リクエスト記入率推移（初回 vs 継続）">
      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="border-b border-[var(--color-border)]">
              <th className="px-3 py-2 text-left font-medium text-[var(--color-text-secondary)]">
                月
              </th>
              <th
                className="px-3 py-2 text-right font-medium text-[var(--color-text-secondary)]"
                colSpan={2}
              >
                初回利用
              </th>
              <th
                className="px-3 py-2 text-right font-medium text-[var(--color-text-secondary)]"
                colSpan={2}
              >
                継続利用
              </th>
            </tr>
            <tr className="border-b border-[var(--color-border)]">
              <th className="px-3 py-1" />
              <th className="px-3 py-1 text-right text-xs text-[var(--color-text-muted)]">件数</th>
              <th className="px-3 py-1 text-right text-xs text-[var(--color-text-muted)]">
                記入率
              </th>
              <th className="px-3 py-1 text-right text-xs text-[var(--color-text-muted)]">件数</th>
              <th className="px-3 py-1 text-right text-xs text-[var(--color-text-muted)]">
                記入率
              </th>
            </tr>
          </thead>
          <tbody>
            {months.map((month) => {
              const f = firstData.find((d) => d.month === month);
              const r = repeatData.find((d) => d.month === month);
              const fRate = f && f.totalOrders > 0 ? (f.withRequest / f.totalOrders) * 100 : 0;
              const rRate = r && r.totalOrders > 0 ? (r.withRequest / r.totalOrders) * 100 : 0;
              return (
                <tr
                  key={month}
                  className="border-b border-[var(--color-border)] hover:bg-[var(--color-bg-elevated)]"
                >
                  <td className="px-3 py-2 font-mono text-xs">{month}</td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {String(f?.totalOrders ?? 0)}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums text-blue-600 dark:text-blue-400">
                    {f?.totalOrders ? `${String(Math.round(fRate))}%` : '-'}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {String(r?.totalOrders ?? 0)}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums text-emerald-600 dark:text-emerald-400">
                    {r?.totalOrders ? `${String(Math.round(rRate))}%` : '-'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <SoWhat>
        初回と継続利用者のリクエスト記入率を月次で比較することで、オンボーディング施策の効果測定が可能。記入率が低下傾向の月はUI変更やキャンペーンの影響を確認する。
      </SoWhat>
    </SectionShell>
  );
}

/** @graph-connects none */
function SceneCrossSection({ data }: { data: SceneSegmentCross[] }) {
  if (data.length === 0) {
    return (
      <SectionShell title="着用シーン × 利用回数クロス集計">
        <EmptyState />
      </SectionShell>
    );
  }

  const scenes = [...new Set(data.map((d) => d.sceneTitle))];
  const firstData = data.filter((d) => d.segment === 'first');
  const repeatData = data.filter((d) => d.segment === 'repeat');

  return (
    <SectionShell title="着用シーン × 利用回数クロス集計">
      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="border-b border-[var(--color-border)]">
              <th className="px-3 py-2 text-left font-medium text-[var(--color-text-secondary)]">
                シーン
              </th>
              <th className="px-3 py-2 text-right font-medium text-[var(--color-text-secondary)]">
                初回
              </th>
              <th className="px-3 py-2 text-right font-medium text-[var(--color-text-secondary)]">
                継続
              </th>
              <th className="px-3 py-2 text-right font-medium text-[var(--color-text-secondary)]">
                合計
              </th>
            </tr>
          </thead>
          <tbody>
            {scenes.map((scene) => {
              const fCount = firstData.find((d) => d.sceneTitle === scene)?.count ?? 0;
              const rCount = repeatData.find((d) => d.sceneTitle === scene)?.count ?? 0;
              return (
                <tr
                  key={scene}
                  className="border-b border-[var(--color-border)] hover:bg-[var(--color-bg-elevated)]"
                >
                  <td className="px-3 py-2 text-[var(--color-text-primary)]">{scene}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{String(fCount)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{String(rCount)}</td>
                  <td className="px-3 py-2 text-right tabular-nums font-medium">
                    {String(fCount + rCount)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <SoWhat>
        初回と継続でシーン指定パターンが異なる場合、利用段階に応じたスタイリング提案戦略を差別化すべき。継続利用者に多いシーンはリピート要因として仕入れ戦略にも反映可能。
      </SoWhat>
    </SectionShell>
  );
}

/** @graph-connects none */
function SectionShell({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h3 className="mb-4 text-sm font-semibold text-[var(--color-text-primary)]">{title}</h3>
      {children}
    </section>
  );
}

/** @graph-connects none */
function SoWhat({ children }: { children: React.ReactNode }) {
  return (
    <div className="mt-3 rounded-lg border-l-4 border-[var(--color-accent-primary)] bg-[var(--color-accent-primary)]/5 px-4 py-3">
      <div className="mb-1 text-xs font-semibold tracking-wide text-[var(--color-accent-primary)] uppercase">
        So What
      </div>
      <p className="text-sm leading-relaxed text-[var(--color-text-secondary)]">{children}</p>
    </div>
  );
}

/** @graph-connects none */
function EmptyState() {
  return (
    <div className="py-8 text-center text-sm text-[var(--color-text-muted)]">
      データがありません
    </div>
  );
}
